import React, { useEffect, useState, useMemo, useCallback } from "react";
import "../assets/scss/layout/admin.scss";

const API_BASE = process.env.REACT_APP_API_BASE || "https://swiftexprofit.com";

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

function fetchWithAuth(url, options = {}) {
  const token = localStorage.getItem("token");
  const headers = options.headers || {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return fetch(url, { ...options, headers: { "Content-Type": "application/json", ...headers } });
}

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** Shared BTC price hook — polls every 30s, used across the whole panel. */
function useBtcPrice() {
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/price/btc`);
      if (!res.ok) throw new Error("bad price response");
      const data = await res.json();
      const p = Number(data.btc_usd);
      if (Number.isFinite(p) && p > 0) setPrice(p);
    } catch {
      // fallback to public source so the USD column is never dead
      try {
        const r = await fetch("https://api.binance.com/api/v3/ticker/price?symbol=BTCUSDT");
        const d = await r.json();
        const p = Number(d.price);
        if (Number.isFinite(p) && p > 0) setPrice(p);
      } catch {
        /* leave null */
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  return { price, loading, reload: load };
}

const fmtUsd = (n) =>
  Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtBtc = (n) => `${Number(n || 0).toFixed(8)} BTC`;

const initials = (s = "") =>
  s.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "?";

/* ------------------------------------------------------------------ */
/* small UI atoms                                                      */
/* ------------------------------------------------------------------ */

function Badge({ tone = "slate", children }) {
  return <span style={{ ...S.badge, ...S.badgeTone[tone] }}>{children}</span>;
}

function Btn({ variant = "ghost", size = "md", style, children, ...rest }) {
  return (
    <button
      {...rest}
      style={{
        ...S.btn,
        ...(size === "sm" ? S.btnSm : null),
        ...S.btnVariant[variant],
        ...(rest.disabled ? S.btnDisabled : null),
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function Field({ label, hint, children }) {
  return (
    <label style={S.field}>
      <span style={S.fieldLabel}>
        {label}
        {hint ? <span style={S.fieldHint}>{hint}</span> : null}
      </span>
      {children}
    </label>
  );
}

function Input(props) {
  const [focus, setFocus] = useState(false);
  return (
    <input
      {...props}
      onFocus={(e) => { setFocus(true); props.onFocus?.(e); }}
      onBlur={(e) => { setFocus(false); props.onBlur?.(e); }}
      style={{ ...S.input, ...(focus ? S.inputFocus : null), ...props.style }}
    />
  );
}

function Modal({ title, subtitle, children, onClose, width = 520 }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div style={S.overlay} onMouseDown={onClose}>
      <div style={{ ...S.modal, maxWidth: width }} onMouseDown={(e) => e.stopPropagation()}>
        <div style={S.modalHead}>
          <div>
            <h3 style={S.modalTitle}>{title}</h3>
            {subtitle ? <p style={S.modalSub}>{subtitle}</p> : null}
          </div>
          <button onClick={onClose} style={S.closeBtn} aria-label="Close">×</button>
        </div>
        <div style={S.modalBody}>{children}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* login                                                               */
/* ------------------------------------------------------------------ */

function LoginModal({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Login failed");
      localStorage.setItem("token", data.token);
      onLogin();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={S.loginOverlay}>
      <div style={S.loginCard}>
        <div style={S.brandRow}>
          <div style={S.brandMark}>SX</div>
          <div>
            <div style={S.brandName}>SwiftEx Admin</div>
            <div style={S.brandSub}>Restricted console</div>
          </div>
        </div>

        <h2 style={S.loginTitle}>Sign in to continue</h2>
        <p style={S.loginLead}>Administrator credentials are required to manage balances and users.</p>

        <form onSubmit={submit} style={{ display: "grid", gap: 14, marginTop: 22 }}>
          <Field label="Email">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="admin@company.com" required />
          </Field>
          <Field label="Password">
            <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="••••••••" required />
          </Field>

          {error ? <div style={S.errorBox}>{error}</div> : null}

          <Btn type="submit" variant="primary" disabled={loading} style={{ width: "100%", marginTop: 4 }}>
            {loading ? "Signing in…" : "Sign in"}
          </Btn>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* user form                                                           */
/* ------------------------------------------------------------------ */

function UserForm({ initial = {}, onSubmit, submitLabel = "Save", btcPrice }) {
  const [username, setUsername] = useState(initial.username || "");
  const [email, setEmail] = useState(initial.email || "");
  const [phone, setPhone] = useState(initial.phone || "");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(initial.is_admin === 1);
  const [balance, setBalance] = useState(initial.balance != null ? String(initial.balance) : "0");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const usdPreview = btcPrice && parseFloat(balance) ? fmtUsd(parseFloat(balance) * btcPrice) : null;

  async function submit(e) {
    e && e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const body = { username, email, phone, is_admin: isAdmin ? 1 : 0 };
      if (password) body.password = password;
      if (balance !== "") body.balance = parseFloat(balance);
      await onSubmit(body);
    } catch (err) {
      setError(err.message || "Failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 14 }}>
      <div style={S.grid2}>
        <Field label="Username">
          <Input value={username} onChange={(e) => setUsername(e.target.value)} required />
        </Field>
        <Field label="Phone">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </Field>
      </div>

      <Field label="Email">
        <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required />
      </Field>

      <Field label="Password" hint={initial.id ? "leave blank to keep current" : "required"}>
        <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder={initial.id ? "••••••••" : ""} />
      </Field>

      <Field label="Balance (BTC)" hint={usdPreview ? `≈ ${usdPreview}` : undefined}>
        <Input value={balance} onChange={(e) => setBalance(e.target.value)} type="number" step="0.00000001" />
      </Field>

      <label style={S.checkRow}>
        <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} style={S.checkbox} />
        <span>
          <strong style={{ color: "#e2e8f0" }}>Administrator</strong>
          <span style={S.checkHint}>Full access to users, balances and transactions.</span>
        </span>
      </label>

      {error ? <div style={S.errorBox}>{error}</div> : null}

      <div style={S.formActions}>
        <Btn type="submit" variant="primary" disabled={loading}>{loading ? "Saving…" : submitLabel}</Btn>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* amount form — dual BTC / USD entry                                  */
/* ------------------------------------------------------------------ */

function AmountForm({ type = "deposit", user, btcPrice, priceLoading, onSubmit }) {
  const [mode, setMode] = useState("BTC"); // BTC | USD
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const num = parseFloat(amount);
  const valid = Number.isFinite(num) && num > 0;

  // Always submit BTC to the API, regardless of the input mode.
  const btcAmount = useMemo(() => {
    if (!valid) return 0;
    if (mode === "BTC") return num;
    return btcPrice ? num / btcPrice : 0;
  }, [valid, num, mode, btcPrice]);

  const usdAmount = useMemo(() => {
    if (!valid) return 0;
    if (mode === "USD") return num;
    return btcPrice ? num * btcPrice : 0;
  }, [valid, num, mode, btcPrice]);

  const currentBtc = parseFloat(user?.balance || 0) || 0;
  const nextBtc = type === "deposit" ? currentBtc + btcAmount : currentBtc - btcAmount;

  const quickUsd = [50, 100, 500, 1000];
  const quickBtc = [0.001, 0.01, 0.05, 0.1];

  async function submit(e) {
    e && e.preventDefault();
    setError("");
    if (!valid) return setError("Enter a valid amount greater than zero.");
    if (mode === "USD" && !btcPrice) return setError("BTC price unavailable — switch to BTC entry.");
    if (btcAmount <= 0) return setError("Converted amount is zero.");
    if (type === "withdraw" && btcAmount > currentBtc) return setError("Amount exceeds the user's available balance.");
    setLoading(true);
    try {
      await onSubmit(Number(btcAmount.toFixed(8)));
    } catch (err) {
      setError(err.message || "Failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 16 }}>
      {/* current balance summary */}
      <div style={S.balanceCard}>
        <div>
          <div style={S.balanceLabel}>Current balance</div>
          <div style={S.balanceBtc}>{fmtBtc(currentBtc)}</div>
          <div style={S.balanceUsd}>{btcPrice ? `≈ ${fmtUsd(currentBtc * btcPrice)}` : "USD unavailable"}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={S.balanceLabel}>BTC / USD</div>
          <div style={S.rateValue}>{priceLoading ? "…" : btcPrice ? fmtUsd(btcPrice) : "—"}</div>
          <div style={S.rateNote}>live · refreshed 30s</div>
        </div>
      </div>

      {/* currency switch */}
      <div style={S.segment}>
        {["BTC", "USD"].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => { setMode(m); setAmount(""); setError(""); }}
            style={{ ...S.segmentBtn, ...(mode === m ? S.segmentBtnActive : null) }}
          >
            Enter in {m}
          </button>
        ))}
      </div>

      <Field label={`Amount (${mode})`}>
        <div style={{ position: "relative" }}>
          <span style={S.inputPrefix}>{mode === "USD" ? "$" : "₿"}</span>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            type="number"
            step={mode === "USD" ? "0.01" : "0.00000001"}
            placeholder={mode === "USD" ? "0.00" : "0.00000000"}
            style={{ paddingLeft: 34, fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em" }}
            required
            autoFocus
          />
        </div>
      </Field>

      <div style={S.quickRow}>
        {(mode === "USD" ? quickUsd : quickBtc).map((q) => (
          <button key={q} type="button" style={S.quickBtn} onClick={() => setAmount(String(q))}>
            {mode === "USD" ? `$${q}` : `${q} ₿`}
          </button>
        ))}
        {type === "withdraw" ? (
          <button
            type="button"
            style={S.quickBtn}
            onClick={() => setAmount(mode === "USD" ? (btcPrice ? (currentBtc * btcPrice).toFixed(2) : "") : String(currentBtc))}
          >
            Max
          </button>
        ) : null}
      </div>

      {/* conversion preview */}
      <div style={S.previewCard}>
        <div style={S.previewRow}>
          <span style={S.previewKey}>Converted amount</span>
          <span style={S.previewVal}>
            {fmtBtc(btcAmount)} <span style={S.previewMuted}>· {btcPrice ? fmtUsd(usdAmount) : "—"}</span>
          </span>
        </div>
        <div style={S.previewRow}>
          <span style={S.previewKey}>Balance after {type}</span>
          <span style={{ ...S.previewVal, color: nextBtc < 0 ? "#f87171" : "#4ade80" }}>
            {fmtBtc(nextBtc)} <span style={S.previewMuted}>· {btcPrice ? fmtUsd(nextBtc * btcPrice) : "—"}</span>
          </span>
        </div>
      </div>

      {error ? <div style={S.errorBox}>{error}</div> : null}

      <div style={S.formActions}>
        <Btn type="submit" variant={type === "deposit" ? "primary" : "danger"} disabled={loading || !valid}>
          {loading ? "Processing…" : `Confirm ${type}`}
        </Btn>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* transaction history                                                 */
/* ------------------------------------------------------------------ */

function TransactionHistory({ userId, btcPrice, priceLoading }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetchWithAuth(`${API_BASE}/admin/transactions/${userId}`);
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.message || "Failed to load transactions");
        }
        const data = await res.json();
        if (alive) setTransactions(data.transactions || []);
      } catch (err) {
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId]);

  const rows = useMemo(
    () => (filter === "all" ? transactions : transactions.filter((t) => t.type === filter)),
    [transactions, filter]
  );

  const totals = useMemo(() => {
    let dep = 0, wit = 0;
    for (const t of transactions) {
      const a = parseFloat(t.amount) || 0;
      if (t.type === "deposit") dep += a; else wit += a;
    }
    return { dep, wit };
  }, [transactions]);

  const formatDate = (d) =>
    new Date(d).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  if (loading) return <div style={S.stateBox}>Loading transactions…</div>;
  if (error) return <div style={S.errorBox}>{error}</div>;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={S.statRow}>
        <div style={S.statCard}>
          <div style={S.statLabel}>Total deposited</div>
          <div style={{ ...S.statValue, color: "#4ade80" }}>{fmtBtc(totals.dep)}</div>
          <div style={S.statSub}>{btcPrice ? fmtUsd(totals.dep * btcPrice) : "—"}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>Total withdrawn</div>
          <div style={{ ...S.statValue, color: "#f87171" }}>{fmtBtc(totals.wit)}</div>
          <div style={S.statSub}>{btcPrice ? fmtUsd(totals.wit * btcPrice) : "—"}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>BTC price</div>
          <div style={S.statValue}>{priceLoading ? "…" : btcPrice ? fmtUsd(btcPrice) : "—"}</div>
          <div style={S.statSub}>{transactions.length} transaction{transactions.length !== 1 ? "s" : ""}</div>
        </div>
      </div>

      <div style={S.segment}>
        {["all", "deposit", "withdraw"].map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} style={{ ...S.segmentBtn, ...(filter === f ? S.segmentBtnActive : null) }}>
            {f[0].toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div style={S.stateBox}>No transactions to show.</div>
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Date</th>
                <th style={S.th}>Type</th>
                <th style={{ ...S.th, textAlign: "right" }}>Amount (BTC)</th>
                <th style={{ ...S.th, textAlign: "right" }}>Value (USD)</th>
                <th style={{ ...S.th, textAlign: "right" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((tx, i) => {
                const amt = parseFloat(tx.amount) || 0;
                const dep = tx.type === "deposit";
                return (
                  <tr key={tx.id ?? i} style={S.tr}>
                    <td style={S.td}>{formatDate(tx.date)}</td>
                    <td style={S.td}><Badge tone={dep ? "green" : "red"}>{dep ? "Deposit" : "Withdraw"}</Badge></td>
                    <td style={{ ...S.td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: dep ? "#4ade80" : "#f87171" }}>
                      {dep ? "+" : "−"}{amt.toFixed(8)}
                    </td>
                    <td style={{ ...S.td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#cbd5e1" }}>
                      {btcPrice ? fmtUsd(amt * btcPrice) : "N/A"}
                    </td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <Badge tone={(tx.status || "completed") === "completed" ? "green" : "amber"}>{tx.status || "completed"}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* main panel                                                          */
/* ------------------------------------------------------------------ */

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(!localStorage.getItem("token"));
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const { price: btcPrice, loading: priceLoading } = useBtcPrice();

  useEffect(() => {
    if (showLogin) return;
    loadUsers(page, limit, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, debouncedSearch, refreshKey, showLogin]);

  async function loadUsers(p = 1, lim = 10, q = "") {
    setLoading(true);
    try {
      if (q && q.length > 0) {
        const res = await fetchWithAuth(`${API_BASE}/users?page=1&limit=100000`);
        if (!res.ok) throw new Error("Failed to fetch users");
        const data = await res.json();
        const ql = q.toLowerCase();
        const filtered = (data.users || []).filter(
          (u) =>
            u.username?.toLowerCase().includes(ql) ||
            u.email?.toLowerCase().includes(ql) ||
            (u.phone || "").toLowerCase().includes(ql)
        );
        setUsers(filtered.slice((p - 1) * lim, p * lim));
        setTotalUsers(filtered.length);
        setTotalPages(Math.ceil(filtered.length / lim) || 1);
        setPage(p);
      } else {
        const res = await fetchWithAuth(`${API_BASE}/users?page=${p}&limit=${lim}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || "Failed to fetch");
        }
        const data = await res.json();
        setUsers(data.users || []);
        setTotalPages(data.totalPages || 1);
        setTotalUsers(data.total ?? (data.totalPages || 1) * lim);
        setPage(data.currentPage || p);
      }
    } catch (err) {
      console.error(err);
      alert(err.message?.toString() || "Failed");
    } finally {
      setLoading(false);
    }
  }

  const pageBtcTotal = useMemo(
    () => users.reduce((s, u) => s + (parseFloat(u.balance) || 0), 0),
    [users]
  );
  const adminCount = useMemo(() => users.filter((u) => u.is_admin === 1).length, [users]);

  function requireAuthRetry() {
    localStorage.removeItem("token");
    setShowLogin(true);
  }

  async function handleAdd(body) {
    const res = await fetchWithAuth(`${API_BASE}/admin/add`, { method: "POST", body: JSON.stringify(body) });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) { requireAuthRetry(); throw new Error("Auth required"); }
      const d = await res.json().catch(() => ({})); throw new Error(d.message || "Add failed");
    }
    setModalType(null);
    setRefreshKey((k) => k + 1);
  }

  async function handleEdit(body) {
    if (!selectedUser) return;
    const res = await fetchWithAuth(`${API_BASE}/admin/update/${selectedUser.id}`, { method: "PUT", body: JSON.stringify(body) });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) { requireAuthRetry(); throw new Error("Auth required"); }
      const d = await res.json().catch(() => ({})); throw new Error(d.message || "Update failed");
    }
    closeModal();
    setRefreshKey((k) => k + 1);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this user? This cannot be undone.")) return;
    const res = await fetchWithAuth(`${API_BASE}/admin/delete/${id}`, { method: "DELETE" });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) { requireAuthRetry(); alert("Auth required"); return; }
      const d = await res.json().catch(() => ({})); alert(d.message || "Delete failed"); return;
    }
    setRefreshKey((k) => k + 1);
  }

  async function handleDeposit(id, amount) {
    const res = await fetchWithAuth(`${API_BASE}/admin/deposit/${id}`, { method: "POST", body: JSON.stringify({ amount }) });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) { requireAuthRetry(); throw new Error("Auth required"); }
      const d = await res.json().catch(() => ({})); throw new Error(d.message || "Deposit failed");
    }
    closeModal();
    setRefreshKey((k) => k + 1);
  }

  async function handleWithdraw(id, amount) {
    const res = await fetchWithAuth(`${API_BASE}/admin/withdraw/${id}`, { method: "POST", body: JSON.stringify({ amount }) });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) { requireAuthRetry(); throw new Error("Auth required"); }
      const d = await res.json().catch(() => ({})); throw new Error(d.message || "Withdraw failed");
    }
    closeModal();
    setRefreshKey((k) => k + 1);
  }

  function closeModal() {
    setModalType(null);
    setSelectedUser(null);
  }

  return (
    <div style={S.page}>
      <div style={S.pageGlow} />

      <div style={S.shell}>
        {/* header */}
        <header style={S.header}>
          <div style={S.brandRow}>
            <div style={S.brandMark}>SX</div>
            <div>
              <h1 style={S.title}>Admin Panel</h1>
              <p style={S.subtitle}>Users, balances & transaction control</p>
            </div>
          </div>

          <div style={S.headerRight}>
            <div style={S.pricePill}>
              <span style={{ ...S.dot, background: btcPrice ? "#22c55e" : "#f59e0b" }} />
              <span style={S.pricePillLabel}>BTC</span>
              <strong style={S.pricePillValue}>{priceLoading ? "…" : btcPrice ? fmtUsd(btcPrice) : "unavailable"}</strong>
            </div>
            <Btn variant="primary" onClick={() => { setSelectedUser(null); setModalType("add"); }}>+ Add user</Btn>
            <Btn onClick={() => { localStorage.removeItem("token"); setShowLogin(true); }}>Logout</Btn>
          </div>
        </header>

        {/* stats */}
        <section style={S.statRow}>
          <div style={S.statCard}>
            <div style={S.statLabel}>Users {search ? "(matching)" : "(total)"}</div>
            <div style={S.statValue}>{totalUsers || users.length}</div>
            <div style={S.statSub}>{adminCount} admin{adminCount !== 1 ? "s" : ""} on this page</div>
          </div>
          <div style={S.statCard}>
            <div style={S.statLabel}>Holdings on this page</div>
            <div style={S.statValue}>{pageBtcTotal.toFixed(8)} ₿</div>
            <div style={S.statSub}>{btcPrice ? fmtUsd(pageBtcTotal * btcPrice) : "USD unavailable"}</div>
          </div>
          <div style={S.statCard}>
            <div style={S.statLabel}>Page</div>
            <div style={S.statValue}>{page} / {totalPages}</div>
            <div style={S.statSub}>{limit} rows per page</div>
          </div>
          <div style={S.statCard}>
            <div style={S.statLabel}>Market rate</div>
            <div style={S.statValue}>{btcPrice ? fmtUsd(btcPrice) : "—"}</div>
            <div style={S.statSub}>auto-refresh every 30s</div>
          </div>
        </section>

        {/* toolbar */}
        <section style={S.card}>
          <div style={S.toolbar}>
            <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 420 }}>
              <span style={S.searchIcon}>⌕</span>
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search username, email or phone…"
                style={{ paddingLeft: 36 }}
              />
            </div>

            <div style={S.pager}>
              <Btn size="sm" disabled={page <= 1 || loading} onClick={() => setPage(1)}>«</Btn>
              <Btn size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</Btn>
              <span style={S.pageIndicator}>{page} / {totalPages}</span>
              <Btn size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>›</Btn>
              <Btn size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(totalPages)}>»</Btn>
              <select
                value={limit}
                onChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(1); }}
                style={S.select}
              >
                {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n} / page</option>)}
              </select>
            </div>
          </div>

          {/* table */}
          {loading ? (
            <div style={S.stateBox}>Loading users…</div>
          ) : users.length === 0 ? (
            <div style={S.stateBox}>No users found.</div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>User</th>
                    <th style={S.th}>Contact</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Balance</th>
                    <th style={S.th}>Role</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const btc = parseFloat(u.balance) || 0;
                    return (
                      <tr key={u.id} style={S.tr}>
                        <td style={S.td}>
                          <div style={S.userCell}>
                            <div style={S.avatar}>{initials(u.username)}</div>
                            <div>
                              <div style={S.userName}>{u.username}</div>
                              <div style={S.userId}>ID #{u.id}</div>
                            </div>
                          </div>
                        </td>
                        <td style={S.td}>
                          <div style={S.userName}>{u.email}</div>
                          <div style={S.userId}>{u.phone || "—"}</div>
                        </td>
                        <td style={{ ...S.td, textAlign: "right" }}>
                          <div style={S.balCellBtc}>{btc.toFixed(8)} ₿</div>
                          <div style={S.balCellUsd}>{btcPrice ? fmtUsd(btc * btcPrice) : "—"}</div>
                        </td>
                        <td style={S.td}>
                          <Badge tone={u.is_admin === 1 ? "violet" : "slate"}>{u.is_admin === 1 ? "Admin" : "User"}</Badge>
                        </td>
                        <td style={{ ...S.td, textAlign: "right" }}>
                          <div style={S.actions}>
                            <Btn size="sm" variant="success" onClick={() => { setSelectedUser(u); setModalType("deposit"); }}>Deposit</Btn>
                            <Btn size="sm" variant="warning" onClick={() => { setSelectedUser(u); setModalType("withdraw"); }}>Withdraw</Btn>
                            <Btn size="sm" onClick={() => { setSelectedUser(u); setModalType("transactions"); }}>History</Btn>
                            <Btn size="sm" onClick={() => { setSelectedUser(u); setModalType("edit"); }}>Edit</Btn>
                            <Btn size="sm" variant="danger" onClick={() => handleDelete(u.id)}>Delete</Btn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <footer style={S.footer}>
          SwiftEx admin console · balances stored in BTC, USD values converted at the live market rate.
        </footer>
      </div>

      {showLogin && <LoginModal onLogin={() => { setShowLogin(false); setRefreshKey((k) => k + 1); }} />}

      {modalType === "add" && (
        <Modal title="Add user" subtitle="Create a new account and set its starting balance." onClose={closeModal}>
          <UserForm onSubmit={handleAdd} submitLabel="Create user" btcPrice={btcPrice} />
        </Modal>
      )}

      {modalType === "edit" && selectedUser && (
        <Modal title={`Edit ${selectedUser.username}`} subtitle={`User ID #${selectedUser.id}`} onClose={closeModal}>
          <UserForm initial={selectedUser} onSubmit={handleEdit} submitLabel="Save changes" btcPrice={btcPrice} />
        </Modal>
      )}

      {modalType === "deposit" && selectedUser && (
        <Modal title={`Deposit to ${selectedUser.username}`} subtitle="Enter the amount in BTC or USD." onClose={closeModal}>
          <AmountForm
            type="deposit"
            user={selectedUser}
            btcPrice={btcPrice}
            priceLoading={priceLoading}
            onSubmit={(amount) => handleDeposit(selectedUser.id, amount)}
          />
        </Modal>
      )}

      {modalType === "withdraw" && selectedUser && (
        <Modal title={`Withdraw from ${selectedUser.username}`} subtitle="Enter the amount in BTC or USD." onClose={closeModal}>
          <AmountForm
            type="withdraw"
            user={selectedUser}
            btcPrice={btcPrice}
            priceLoading={priceLoading}
            onSubmit={(amount) => handleWithdraw(selectedUser.id, amount)}
          />
        </Modal>
      )}

      {modalType === "transactions" && selectedUser && (
        <Modal title={`${selectedUser.username} · transactions`} subtitle={`User ID #${selectedUser.id}`} onClose={closeModal} width={860}>
          <TransactionHistory userId={selectedUser.id} btcPrice={btcPrice} priceLoading={priceLoading} />
        </Modal>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* styles                                                              */
/* ------------------------------------------------------------------ */

const S = {
  page: {
    position: "relative",
    minHeight: "100vh",
    background: "radial-gradient(1200px 600px at 12% -10%, #12233a 0%, transparent 55%), #060910",
    color: "#e2e8f0",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "32px 20px 56px",
  },
  pageGlow: {
    position: "fixed", inset: 0, pointerEvents: "none",
    background: "radial-gradient(700px 380px at 88% 0%, rgba(34,197,94,0.10), transparent 60%)",
  },
  shell: { position: "relative", maxWidth: 1360, margin: "0 auto", display: "grid", gap: 20 },

  header: { display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between" },
  brandRow: { display: "flex", alignItems: "center", gap: 14 },
  brandMark: {
    width: 46, height: 46, borderRadius: 14, display: "grid", placeItems: "center",
    background: "linear-gradient(135deg,#22c55e,#0ea5e9)", color: "#06110b", fontWeight: 800, fontSize: 16,
    boxShadow: "0 10px 30px -12px rgba(34,197,94,0.8)",
  },
  brandName: { fontWeight: 700, fontSize: 15, color: "#f1f5f9" },
  brandSub: { fontSize: 12, color: "#64748b" },
  title: { margin: 0, fontSize: 26, fontWeight: 750, letterSpacing: "-0.02em", color: "#f8fafc" },
  subtitle: { margin: "3px 0 0", fontSize: 13, color: "#7c8ba1" },
  headerRight: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 },

  pricePill: {
    display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", borderRadius: 999,
    background: "rgba(148,163,184,0.07)", border: "1px solid rgba(148,163,184,0.16)", fontSize: 13,
  },
  pricePillLabel: { color: "#94a3b8", fontWeight: 600, letterSpacing: "0.04em", fontSize: 11 },
  pricePillValue: { color: "#f1f5f9", fontVariantNumeric: "tabular-nums" },
  dot: { width: 8, height: 8, borderRadius: "50%" },

  statRow: { display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" },
  statCard: {
    padding: "16px 18px", borderRadius: 16,
    background: "linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015))",
    border: "1px solid rgba(148,163,184,0.14)",
  },
  statLabel: { fontSize: 11.5, textTransform: "uppercase", letterSpacing: "0.08em", color: "#7c8ba1", fontWeight: 600 },
  statValue: { fontSize: 22, fontWeight: 700, color: "#f8fafc", marginTop: 8, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" },
  statSub: { fontSize: 12.5, color: "#94a3b8", marginTop: 4 },

  card: {
    borderRadius: 20, border: "1px solid rgba(148,163,184,0.14)",
    background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.012))",
    overflow: "hidden",
  },
  toolbar: {
    display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between",
    padding: 16, borderBottom: "1px solid rgba(148,163,184,0.12)",
  },
  searchIcon: { position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "#64748b", fontSize: 16 },
  pager: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  pageIndicator: { fontSize: 13, color: "#94a3b8", padding: "0 8px", fontVariantNumeric: "tabular-nums" },
  select: {
    background: "rgba(15,23,42,0.9)", color: "#e2e8f0", border: "1px solid rgba(148,163,184,0.2)",
    borderRadius: 10, padding: "8px 10px", fontSize: 13, outline: "none", cursor: "pointer",
  },

  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 880 },
  th: {
    textAlign: "left", fontSize: 11.5, textTransform: "uppercase", letterSpacing: "0.08em",
    color: "#7c8ba1", fontWeight: 600, padding: "14px 18px", borderBottom: "1px solid rgba(148,163,184,0.12)",
    whiteSpace: "nowrap",
  },
  tr: { borderBottom: "1px solid rgba(148,163,184,0.07)" },
  td: { padding: "14px 18px", fontSize: 13.5, color: "#cbd5e1", verticalAlign: "middle" },

  userCell: { display: "flex", alignItems: "center", gap: 12 },
  avatar: {
    width: 38, height: 38, borderRadius: 12, display: "grid", placeItems: "center", flexShrink: 0,
    background: "linear-gradient(135deg, rgba(34,197,94,0.25), rgba(14,165,233,0.25))",
    border: "1px solid rgba(148,163,184,0.18)", color: "#e2e8f0", fontWeight: 700, fontSize: 13,
  },
  userName: { color: "#f1f5f9", fontWeight: 600 },
  userId: { color: "#64748b", fontSize: 12, marginTop: 2 },
  balCellBtc: { color: "#f8fafc", fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  balCellUsd: { color: "#4ade80", fontSize: 12.5, marginTop: 3, fontVariantNumeric: "tabular-nums" },
  actions: { display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" },

  footer: { textAlign: "center", fontSize: 12.5, color: "#5b6a80", paddingTop: 6 },

  /* atoms */
  btn: {
    appearance: "none", border: "1px solid rgba(148,163,184,0.2)", background: "rgba(148,163,184,0.08)",
    color: "#e2e8f0", borderRadius: 10, padding: "10px 16px", fontSize: 13.5, fontWeight: 600,
    cursor: "pointer", transition: "all .16s ease", whiteSpace: "nowrap", lineHeight: 1.1,
  },
  btnSm: { padding: "7px 11px", fontSize: 12.5, borderRadius: 9 },
  btnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  btnVariant: {
    ghost: {},
    primary: { background: "linear-gradient(135deg,#22c55e,#16a34a)", border: "1px solid rgba(34,197,94,0.5)", color: "#04120a", boxShadow: "0 10px 26px -14px rgba(34,197,94,0.9)" },
    success: { background: "rgba(34,197,94,0.14)", border: "1px solid rgba(34,197,94,0.35)", color: "#4ade80" },
    warning: { background: "rgba(245,158,11,0.13)", border: "1px solid rgba(245,158,11,0.32)", color: "#fbbf24" },
    danger: { background: "rgba(239,68,68,0.13)", border: "1px solid rgba(239,68,68,0.32)", color: "#f87171" },
  },

  badge: { display: "inline-block", padding: "4px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.02em", textTransform: "capitalize" },
  badgeTone: {
    slate: { background: "rgba(148,163,184,0.12)", color: "#94a3b8" },
    green: { background: "rgba(34,197,94,0.14)", color: "#4ade80" },
    red: { background: "rgba(239,68,68,0.14)", color: "#f87171" },
    amber: { background: "rgba(245,158,11,0.14)", color: "#fbbf24" },
    violet: { background: "rgba(139,92,246,0.16)", color: "#c4b5fd" },
  },

  field: { display: "grid", gap: 7 },
  fieldLabel: { fontSize: 12.5, color: "#94a3b8", fontWeight: 600, display: "flex", justifyContent: "space-between", gap: 10 },
  fieldHint: { color: "#64748b", fontWeight: 500 },
  input: {
    width: "100%", boxSizing: "border-box", background: "rgba(2,6,23,0.6)",
    border: "1px solid rgba(148,163,184,0.2)", borderRadius: 11, padding: "11px 13px",
    color: "#f1f5f9", fontSize: 14, outline: "none", transition: "border-color .16s, box-shadow .16s",
  },
  inputFocus: { borderColor: "rgba(34,197,94,0.6)", boxShadow: "0 0 0 3px rgba(34,197,94,0.12)" },
  inputPrefix: { position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 16, fontWeight: 700 },
  grid2: { display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))" },
  checkRow: { display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "#94a3b8", cursor: "pointer" },
  checkbox: { width: 17, height: 17, accentColor: "#22c55e", marginTop: 2 },
  checkHint: { display: "block", fontSize: 12.5, color: "#64748b", marginTop: 2 },
  formActions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 },

  errorBox: { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", borderRadius: 11, padding: "10px 13px", fontSize: 13 },
  stateBox: { padding: "38px 20px", textAlign: "center", color: "#7c8ba1", fontSize: 13.5 },

  /* amount form */
  balanceCard: {
    display: "flex", justifyContent: "space-between", gap: 16, padding: "16px 18px", borderRadius: 16,
    background: "linear-gradient(135deg, rgba(34,197,94,0.09), rgba(14,165,233,0.06))",
    border: "1px solid rgba(148,163,184,0.16)",
  },
  balanceLabel: { fontSize: 11.5, textTransform: "uppercase", letterSpacing: "0.08em", color: "#7c8ba1", fontWeight: 600 },
  balanceBtc: { fontSize: 20, fontWeight: 700, color: "#f8fafc", marginTop: 7, fontVariantNumeric: "tabular-nums" },
  balanceUsd: { fontSize: 13, color: "#4ade80", marginTop: 3 },
  rateValue: { fontSize: 18, fontWeight: 700, color: "#f1f5f9", marginTop: 7, fontVariantNumeric: "tabular-nums" },
  rateNote: { fontSize: 11.5, color: "#64748b", marginTop: 3 },

  segment: { display: "flex", gap: 4, padding: 4, borderRadius: 12, background: "rgba(2,6,23,0.6)", border: "1px solid rgba(148,163,184,0.14)" },
  segmentBtn: {
    flex: 1, padding: "9px 12px", borderRadius: 9, border: "none", background: "transparent",
    color: "#94a3b8", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all .16s ease",
  },
  segmentBtnActive: { background: "rgba(34,197,94,0.16)", color: "#4ade80", boxShadow: "inset 0 0 0 1px rgba(34,197,94,0.32)" },

  quickRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  quickBtn: {
    padding: "7px 13px", borderRadius: 999, border: "1px solid rgba(148,163,184,0.2)",
    background: "rgba(148,163,184,0.07)", color: "#cbd5e1", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
  },

  previewCard: { display: "grid", gap: 10, padding: "14px 16px", borderRadius: 14, background: "rgba(2,6,23,0.55)", border: "1px solid rgba(148,163,184,0.14)" },
  previewRow: { display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13 },
  previewKey: { color: "#7c8ba1" },
  previewVal: { color: "#f1f5f9", fontWeight: 600, fontVariantNumeric: "tabular-nums" },
  previewMuted: { color: "#94a3b8", fontWeight: 500 },

  /* modal */
  overlay: {
    position: "fixed", inset: 0, background: "rgba(2,6,23,0.75)", backdropFilter: "blur(6px)",
    display: "grid", placeItems: "center", padding: 20, zIndex: 1000, overflowY: "auto",
  },
  modal: {
    width: "100%", borderRadius: 20, border: "1px solid rgba(148,163,184,0.16)",
    background: "linear-gradient(180deg,#0e1626,#080d18)", boxShadow: "0 40px 90px -30px rgba(0,0,0,0.9)",
    maxHeight: "90vh", display: "flex", flexDirection: "column",
  },
  modalHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, padding: "20px 22px 14px", borderBottom: "1px solid rgba(148,163,184,0.12)" },
  modalTitle: { margin: 0, fontSize: 17.5, fontWeight: 700, color: "#f8fafc", letterSpacing: "-0.01em" },
  modalSub: { margin: "4px 0 0", fontSize: 12.5, color: "#7c8ba1" },
  closeBtn: { background: "transparent", border: "none", color: "#7c8ba1", fontSize: 26, lineHeight: 1, cursor: "pointer", padding: 0 },
  modalBody: { padding: 22, overflowY: "auto" },

  /* login */
  loginOverlay: {
    position: "fixed", inset: 0, zIndex: 1100, display: "grid", placeItems: "center", padding: 20,
    background: "radial-gradient(900px 500px at 50% -10%, #14263f 0%, #05080f 60%)",
  },
  loginCard: {
    width: "100%", maxWidth: 420, padding: 30, borderRadius: 22,
    border: "1px solid rgba(148,163,184,0.16)", background: "linear-gradient(180deg,#0e1626,#080d18)",
    boxShadow: "0 40px 90px -30px rgba(0,0,0,0.9)",
  },
  loginTitle: { margin: "24px 0 0", fontSize: 21, fontWeight: 700, color: "#f8fafc", letterSpacing: "-0.02em" },
  loginLead: { margin: "6px 0 0", fontSize: 13, color: "#7c8ba1", lineHeight: 1.5 },
};
